import { REAUTH_MAX_AGE_SECONDS, type SessionInfo } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';
import {
  fetchSession,
  login as loginRequest,
  logout as logoutRequest,
  updatePreferences,
} from '../api/auth.js';
import { isUnauthenticated } from '../api/client.js';
import {
  SESSION_QUERY_KEY,
  SessionContext,
  type SessionContextValue,
  type SessionStatus,
} from './session-context.js';

/**
 * Who is signed in (knowledge doc section 17).
 *
 * The session itself lives in an `HttpOnly` cookie, so this holds no credential —
 * only the API's answer to "who am I", refetched on load and after every change.
 * That makes a stale tab self-correcting: a revoked session fails its next request
 * and the app falls back to the login screen rather than showing a shell the API
 * will refuse to fill.
 */

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchSession(signal),
    // A 401 here is the ordinary signed-out case, not a transient failure worth
    // three more round trips.
    retry: false,
    refetchOnWindowFocus: true,
  });

  const loginMutation = useMutation({
    mutationFn: ({
      email,
      password,
      totpCode,
    }: {
      email: string;
      password: string;
      totpCode?: string | undefined;
    }) => loginRequest(email, password, totpCode),
    onSuccess: (session) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      // Whatever the API said, this browser is done with that session. Clearing
      // everything matters as much as clearing the session: cached server lists and
      // events must not survive into the next person's sign-in on a shared machine.
      queryClient.clear();
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
    },
  });

  const session = query.data ?? null;

  const status: SessionStatus = useMemo(() => {
    if (query.isPending) return 'loading';
    if (session !== null) return 'signed-in';
    return 'signed-out';
  }, [query.isPending, session]);

  const signIn = useCallback(
    async (email: string, password: string, totpCode?: string): Promise<void> => {
      await loginMutation.mutateAsync({ email, password, totpCode });
    },
    [loginMutation],
  );

  const applySession = useCallback(
    (updated: SessionInfo): void => {
      queryClient.setQueryData(SESSION_QUERY_KEY, updated);
    },
    [queryClient],
  );

  /**
   * Never rejects. `onSettled` has already cleared this browser's state by the time
   * the promise settles, so there is nothing for a caller to do about a failed call
   * — and leaving it to reject would only produce an unhandled rejection in the
   * console of someone who has, as far as they can tell, signed out successfully.
   */
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      // Logged by the mutation; the local session is gone either way.
    }
  }, [logoutMutation]);

  const timezoneMutation = useMutation({
    mutationFn: updatePreferences,
    onSuccess: (updated) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, updated);
    },
  });

  const setTimezone = useCallback(
    async (timezone: string | null): Promise<void> => {
      await timezoneMutation.mutateAsync(timezone);
    },
    [timezoneMutation],
  );

  const noteReauthenticated = useCallback(
    (at: string): void => {
      queryClient.setQueryData<SessionInfo>(SESSION_QUERY_KEY, (current) =>
        current === undefined ? current : { ...current, reauthenticatedAt: at },
      );
    },
    [queryClient],
  );

  const hasFreshReauth = useCallback((): boolean => {
    const at = session?.reauthenticatedAt;
    if (at === null || at === undefined) return false;
    const age = Date.now() - new Date(at).getTime();
    // A little stricter than the API's window, so a request does not fail the gate
    // in the second between deciding not to ask and arriving.
    return age < (REAUTH_MAX_AGE_SECONDS - 15) * 1000;
  }, [session]);

  const loadError =
    query.isError && !isUnauthenticated(query.error) && query.error instanceof Error
      ? query.error
      : null;

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      session,
      signIn,
      signOut,
      applySession,
      setTimezone,
      noteReauthenticated,
      hasFreshReauth,
      loadError,
    }),
    [
      status,
      session,
      signIn,
      signOut,
      applySession,
      setTimezone,
      noteReauthenticated,
      hasFreshReauth,
      loadError,
    ],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
